import React, { useEffect, useState } from 'react';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { db } from '../firebase';

export default function MatchPointsSummary() {
  const [seasons, setSeasons] = useState([]);
  const [pennants, setPennants] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [selectedPennant, setSelectedPennant] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [matchDetails, setMatchDetails] = useState(null);

  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        setLoading(true);
        const seasonsSnapshot = await getDocs(collection(db, 'Seasons'));
        const seasonsList = seasonsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setSeasons(seasonsList);
      } catch (error) {
        console.error('Error fetching seasons:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSeasons();
  }, []);

  useEffect(() => {
    const fetchPennants = async () => {
      if (selectedSeason) {
        try {
          setLoading(true);
          const pennantsSnapshot = await getDocs(collection(db, 'Pennants'));
          const pennantsList = pennantsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          setPennants(pennantsList);
        } catch (error) {
          console.error('Error fetching pennants:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setPennants([]);
      }
    };

    fetchPennants();
  }, [selectedSeason]);

  useEffect(() => {
    const fetchResults = async () => {
      if (selectedSeason && selectedPennant) {
        try {
          setLoading(true);
          const resultsSnapshot = await getDocs(collection(db, 'Results'));
          const resultsList = resultsSnapshot.docs
            .filter((doc) => doc.data().seasonId === selectedSeason && doc.data().pennantId === selectedPennant)
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));

          setResults(resultsList);
        } catch (error) {
          console.error('Error fetching results:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchResults();
  }, [selectedSeason, selectedPennant]);

  const handleMatchClick = async (resultId, matchIndex) => {
    const resultDoc = await getDoc(doc(db, 'Results', resultId));
    if (resultDoc.exists()) {
      const matchData = resultDoc.data().matches[matchIndex];
      setMatchDetails(matchData);
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setMatchDetails(null);
  };

  const getTotalPoints = (player) =>
    results.reduce((total, result) => {
      const match = result.matches.find(
        (match) => match.player1 === player || match.player2 === player
      );
      if (match) {
        const playerPoints = match.player1 === player ? match.player1Points : match.player2Points;
        return total + playerPoints;
      }
      return total;
    }, 0);

  const players = Array.from(
    new Set(
      results.flatMap((result) =>
        result.matches.flatMap((match) => [match.player1, match.player2])
      )
    )
  );

  const sortedPlayers = players.sort((a, b) => getTotalPoints(b) - getTotalPoints(a));

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Match Results
      </Typography>
      <FormControl fullWidth margin="normal">
        <InputLabel id="season-select-label">Select Season</InputLabel>
        <Select
          labelId="season-select-label"
          value={selectedSeason}
          onChange={(e) => setSelectedSeason(e.target.value)}
        >
          {seasons.map((season) => (
            <MenuItem key={season.id} value={season.id}>
              {season.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth margin="normal">
        <InputLabel id="pennant-select-label">Select Pennant</InputLabel>
        <Select
          labelId="pennant-select-label"
          value={selectedPennant}
          onChange={(e) => setSelectedPennant(e.target.value)}
          disabled={!selectedSeason}
        >
          {pennants.map((pennant) => (
            <MenuItem key={pennant.id} value={pennant.id}>
              {pennant.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Player</TableCell>
              {results.map((result) => (
                <TableCell key={result.id}>{result.weekId}</TableCell>
              ))}
              <TableCell>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPlayers.map((player) => (
              <TableRow key={player}>
                <TableCell>{player}</TableCell>
                {results.map((result, resultIndex) => {
                  const match = result.matches.find(
                    (match) => match.player1 === player || match.player2 === player
                  );
                  const matchPoints =
                    match?.player1 === player ? match.player1Points : match?.player2Points || 0;
                  return (
                    <TableCell key={`${player}-week-${resultIndex}`}>
                      <Button onClick={() => handleMatchClick(result.id, resultIndex)}>
                        {matchPoints}
                      </Button>
                    </TableCell>
                  );
                })}
                <TableCell>{getTotalPoints(player)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog for detailed match information */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Match Details</DialogTitle>
        {matchDetails && (
          <DialogContent>
            <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
              <Typography variant="h6">{matchDetails.player1}</Typography>
              <Typography variant="h6">vs</Typography>
              <Typography variant="h6">{matchDetails.player2}</Typography>
            </Box>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Game</TableCell>
                    <TableCell>{matchDetails.player1}</TableCell>
                    <TableCell>{matchDetails.player2}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(matchDetails.player1Scores || {}).map((game, index) => (
                    <TableRow key={index}>
                      <TableCell>{game}</TableCell>
                      <TableCell>{matchDetails.player1Scores[game]}</TableCell>
                      <TableCell>{matchDetails.player2Scores[game]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
